import { c as _c } from "react/compiler-runtime";
import { ArrowDownIcon, ChevronUpIcon, IconButton, Skeleton } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { InfoBarItem } from "@components/common/info-bar/info-bar-item";
import { InfoBarLayoutProvider } from "@components/common/info-bar/info-bar-layout-context";
import { InfoBarValue } from "@components/common/info-bar/info-bar-value";
import { CommonStyles } from "@styles/common-styles";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useState } from "react";
/** Space above the bar, which a page without one keeps above its content to line up with it */
export const INFO_BAR_TOP_SPACING = 1.5;

/** Height of a single-row bar, so all info bars line up across pages */
const BAR_MIN_HEIGHT = "5rem";
function renderContent(content) {
  const isPlainValue = typeof content === "string" || typeof content === "number";
  return isPlainValue ? <InfoBarValue>{content}</InfoBarValue> : content;
}

/**
 * A grey bar with a few labelled values about the current page and an optional action.
 * From `singleRowFrom` up it is one row of a fixed height; below that it wraps, and on phones
 * everything stacks, showing only the first item until the user expands the bar.
 */
export function InfoBar(t0) {
  const $ = _c(32);
  const {
    items: t1,
    action,
    isLoading: t2,
    singleRowFrom: t3
  } = t0;
  const items = t1 === undefined ? [] : t1;
  const isLoading = t2 === undefined ? false : t2;
  const singleRowFrom = t3 === undefined ? "md" : t3;
  const {
    isScreenXs
  } = useMediaQueryList();
  const [isExpanded, setIsExpanded] = useState(false);
  if (isLoading) {
    let t4;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Skeleton variant="rectangular" height={BAR_MIN_HEIGHT} sx={{
        mt: INFO_BAR_TOP_SPACING
      }} />;
      $[0] = t4;
    } else {
      t4 = $[0];
    }
    return t4;
  }
  const visibleItems = items.filter(_temp);
  const isCollapsible = isScreenXs && visibleItems.length > 1;
  const shownItems = isCollapsible && !isExpanded ? visibleItems.slice(0, 1) : visibleItems;
  const hasAction = Boolean(action && !action.isHidden);
  const T0 = InfoBarLayoutProvider;
  const T1 = Box;
  let t4;
  if ($[1] !== singleRowFrom) {
    t4 = {
      mt: INFO_BAR_TOP_SPACING,
      py: {
        xs: 0,
        sm: 1.5,
        [singleRowFrom]: 1
      },
      minHeight: {
        [singleRowFrom]: BAR_MIN_HEIGHT
      },
      backgroundColor: "surfaces.secondary",
      display: "flex",
      alignItems: "center"
    };
    $[1] = singleRowFrom;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const T2 = Box;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      xs: "column",
      sm: "row"
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== singleRowFrom) {
    t6 = {
      xs: "nowrap",
      sm: "wrap",
      [singleRowFrom]: "nowrap"
    };
    $[4] = singleRowFrom;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      xs: "stretch",
      sm: "center"
    };
    t8 = {
      xs: 0,
      sm: 1.5
    };
    $[6] = t7;
    $[7] = t8;
  } else {
    t7 = $[6];
    t8 = $[7];
  }
  let t9;
  if ($[8] !== singleRowFrom) {
    t9 = {
      sm: "hidden",
      [singleRowFrom]: "visible"
    };
    $[8] = singleRowFrom;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t6 || $[11] !== t9) {
    t10 = {
      display: "flex",
      flexDirection: t5,
      flexWrap: t6,
      alignItems: t7,
      rowGap: t8,
      overflow: t9,
      flex: "1 1 auto",
      minWidth: 0
    };
    $[10] = t6;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== isCollapsible || $[14] !== isExpanded) {
    t11 = (t12, index) => {
      const {
        label,
        content,
        isFlexible
      } = t12;
      const item_0 = <InfoBarItem key={label ?? index} label={label} isFlexible={isFlexible}>{renderContent(content)}</InfoBarItem>;
      if (!isCollapsible || index > 0) {
        return item_0;
      }
      return <Box key={label ?? index} sx={{
        display: "flex",
        alignItems: "center",
        pr: 1.5
      }}><Box sx={{
          flex: 1,
          minWidth: 0
        }}>{item_0}</Box><IconButton size="s" aria-label={isExpanded ? "Show less" : "Show more"} aria-expanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)}>{isExpanded ? <ChevronUpIcon /> : <ArrowDownIcon />}</IconButton></Box>;
    };
    $[13] = isCollapsible;
    $[14] = isExpanded;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = shownItems.map(t11);
  let t13;
  if ($[16] !== action?.content || $[17] !== hasAction) {
    t13 = hasAction && <Box sx={{
      px: 2.5,
      py: {
        xs: 1.5,
        sm: 0
      },
      ml: {
        xs: 0,
        sm: "auto"
      },
      flex: "0 0 auto",
      borderTop: {
        xs: CommonStyles.Borders.divider,
        sm: "none"
      },
      "& button": {
        width: {
          xs: "100%",
          sm: "auto"
        }
      }
    }}>{action?.content}</Box>;
    $[16] = action?.content;
    $[17] = hasAction;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== T2 || $[20] !== t10 || $[21] !== t12 || $[22] !== t13) {
    t14 = <T2 sx={t10}>{t12}{t13}</T2>;
    $[19] = T2;
    $[20] = t10;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== T1 || $[25] !== t14 || $[26] !== t4) {
    t15 = <T1 sx={t4}>{t14}</T1>;
    $[24] = T1;
    $[25] = t14;
    $[26] = t4;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== T0 || $[29] !== singleRowFrom || $[30] !== t15) {
    t16 = <T0 value={singleRowFrom}>{t15}</T0>;
    $[28] = T0;
    $[29] = singleRowFrom;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  return t16;
}
function _temp(item) {
  return !item.isHidden;
}